import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { TokenService } from '../auth/token.service';
import { AuthService } from '../auth/auth.service';

const refreshing$ = new BehaviorSubject<boolean>(false);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);

  const token = tokenService.getAccessToken();
  const authReq = token ? addBearerToken(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403 && !req.url.includes('/auth/')) {
        authService.logout();
        return throwError(() => error);
      }
      if (error.status !== 401 || req.url.includes('/auth/')) {
        return throwError(() => error);
      }

      if (refreshing$.getValue()) {
        // Queue: wait for refresh to complete, then retry with new token
        return refreshing$.pipe(
          filter((isRefreshing) => !isRefreshing),
          take(1),
          switchMap(() => {
            const newToken = tokenService.getAccessToken();
            return next(newToken ? addBearerToken(req, newToken) : req);
          }),
        );
      }

      refreshing$.next(true);
      return authService.refresh().pipe(
        switchMap((res) => {
          refreshing$.next(false);
          const newToken = res.data?.accessToken;
          return next(newToken ? addBearerToken(req, newToken) : req);
        }),
        catchError((refreshError) => {
          refreshing$.next(false);
          authService.logout();
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};

function addBearerToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
